import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { API_URL } from '../../Config';

const CreerEvenement = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [showNotification, setShowNotification] = useState(false);
  const [errors, setErrors] = useState({});

  const [beneficiaires, setBeneficiaires] = useState([]);
  const [packsEnterrement, setPacksEnterrement] = useState([]);
  const [materielsDisponibles, setMaterielsDisponibles] = useState([]);
  
  const [formData, setFormData] = useState({
    type: 'enterrement',
    nom_type_custom: '',
    beneficiaire_id: '',
    pack_enterrement_id: '',
    transport: false,
    materiels: []
  });

  const [nouveauMateriel, setNouveauMateriel] = useState({
    materiel_id: '',
    quantite: 1
  });

  useEffect(() => {
    chargerDonnees();
  }, []);

  const chargerDonnees = async () => {
    try {
      setLoadingData(true);
      const token = localStorage.getItem('token');
      const config = {
        headers: { Authorization: `Bearer ${token}` }
      };

      const [beneficiairesRes, packsRes, materielsRes] = await Promise.all([
        axios.get(`${API_URL}/beneficiaires`, config),
        axios.get(`${API_URL}/packs-enterrement`, config),
        axios.get(`${API_URL}/materiels?disponible=true`, config)
      ]);

      setBeneficiaires(beneficiairesRes.data?.data || beneficiairesRes.data || []);
      setPacksEnterrement(packsRes.data?.data || packsRes.data || []);
      setMaterielsDisponibles(materielsRes.data?.data || materielsRes.data || []);

    } catch (error) {
      console.error('Erreur chargement données:', error);
      showMessage('Erreur lors du chargement des données', 'error');
    } finally {
      setLoadingData(false);
    }
  };

  const showMessage = (text, type) => {
    setMessage({ type, text });
    setShowNotification(true);
    setTimeout(() => setShowNotification(false), 4000);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    setFormData(prev => {
      let updatedData = {
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      };
      
      if (name === 'type') {
        updatedData = {
          ...updatedData,
          pack_enterrement_id: '',
          nom_type_custom: '',
          materiels: []
        };
      }
      
      if (name === 'pack_enterrement_id' && value) {
        updatedData = {
          ...updatedData,
          materiels: []
        };
      }
      
      return updatedData;
    });
    
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const handleMaterielChange = (e) => {
    const { name, value } = e.target;
    setNouveauMateriel(prev => ({
      ...prev,
      [name]: name === 'quantite' ? Math.max(1, parseInt(value) || 1) : value
    }));
  };

  const ajouterMateriel = () => {
    if (!nouveauMateriel.materiel_id) {
      showMessage('Veuillez sélectionner un matériel', 'error');
      return;
    }

    if (formData.pack_enterrement_id) {
      showMessage('Impossible d\'ajouter des matériels manuels avec un pack sélectionné', 'error');
      return;
    }

    const materielSelectionne = materielsDisponibles.find(
      m => m.id.toString() === nouveauMateriel.materiel_id
    );

    if (!materielSelectionne) {
      showMessage('Matériel non trouvé', 'error');
      return;
    }

    const quantiteExistante = formData.materiels.find(m => m.id === materielSelectionne.id)?.quantite || 0;
    const quantiteTotale = quantiteExistante + nouveauMateriel.quantite;
    
    if (quantiteTotale > materielSelectionne.stock) {
      showMessage(`Stock insuffisant. Disponible: ${materielSelectionne.stock - quantiteExistante}`, 'error');
      return;
    }

    setFormData(prev => {
      const existeDeja = prev.materiels.find(m => m.id === materielSelectionne.id);
      
      if (existeDeja) {
        return {
          ...prev,
          materiels: prev.materiels.map(m =>
            m.id === materielSelectionne.id
              ? { ...m, quantite: m.quantite + nouveauMateriel.quantite }
              : m
          )
        };
      } else {
        return {
          ...prev,
          materiels: [
            ...prev.materiels,
            {
              id: materielSelectionne.id,
              nom: materielSelectionne.nom,
              reference: materielSelectionne.reference,
              quantite: nouveauMateriel.quantite,
              stock: materielSelectionne.stock
            }
          ]
        };
      }
    });

    setNouveauMateriel({ materiel_id: '', quantite: 1 });
  };

  const modifierQuantiteMateriel = (id, delta) => {
    setFormData(prev => {
      const newMateriels = prev.materiels.map(m => {
        if (m.id === id) {
          const nouvelleQuantite = m.quantite + delta;
          if (nouvelleQuantite < 1) return m;
          
          const materielOriginal = materielsDisponibles.find(mat => mat.id === id);
          if (materielOriginal && nouvelleQuantite > materielOriginal.stock) {
            showMessage(`Quantité maximale: ${materielOriginal.stock}`, 'error');
            return m;
          }
          
          return { ...m, quantite: nouvelleQuantite };
        }
        return m;
      });
      
      return { ...prev, materiels: newMateriels };
    });
  };

  const supprimerMateriel = (id) => {
    setFormData(prev => ({
      ...prev,
      materiels: prev.materiels.filter(m => m.id !== id)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors({});
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      const user = JSON.parse(localStorage.getItem('user'));
      
      if (!user?.id) {
        throw new Error('Utilisateur non authentifié');
      }

      const dataToSend = {
        user_id: user.id,
        type: formData.type === 'autre' ? 'autre' : formData.type,
        beneficiaire_id: formData.beneficiaire_id || null,
        pack_enterrement_id: formData.pack_enterrement_id || null,
        transport: formData.transport,
        materiels: formData.materiels.map(m => ({
          id: m.id,
          quantite: m.quantite
        }))
      };

      if (formData.type === 'autre') {
        if (!formData.nom_type_custom.trim()) {
          setErrors({ nom_type_custom: ['Le nom du type est requis'] });
          showMessage('Veuillez saisir le nom du type', 'error');
          setLoading(false);
          return;
        }
        dataToSend.nom_type_custom = formData.nom_type_custom;
      }

      const config = {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      };

      await axios.post(`${API_URL}/evenements`, dataToSend, config);

      showMessage('Événement créé avec succès', 'success');
      
      setFormData({
        type: 'enterrement',
        nom_type_custom: '',
        beneficiaire_id: '',
        pack_enterrement_id: '',
        transport: false,
        materiels: []
      });

      setTimeout(() => {
        navigate('client/mes-evenements');
      }, 1500);

    } catch (error) {
      console.error('Erreur création événement:', error);
      
      if (error.response?.status === 422) {
        setErrors(error.response.data.errors || {});
        const firstError = Object.values(error.response.data.errors || {})[0]?.[0];
        showMessage(firstError || 'Erreur de validation', 'error');
      } else if (error.response?.data?.message) {
        showMessage(error.response.data.message, 'error');
      } else {
        showMessage('Erreur lors de la création', 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  const ajoutMaterielsDesactive = formData.pack_enterrement_id;

  const materielsFiltres = materielsDisponibles.filter(materiel => {
    const quantiteExistante = formData.materiels.find(m => m.id === materiel.id)?.quantite || 0;
    return materiel.stock > quantiteExistante;
  });

  if (loadingData) {
    return (
      <div className="flex items-center justify-center min-h-screen ">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement des données...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen  py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* En-tête */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900">Créer un événement</h1>
          <p className="text-gray-600 mt-2">Remplissez les informations pour créer un nouvel événement</p>
        </div>

        {/* Notification */}
        {showNotification && (
          <div className={`mb-6 p-4 rounded-lg ${message.type === 'success' ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
            <div className="flex items-center">
              {message.type === 'success' ? (
                <svg className="w-5 h-5 text-green-500 mr-3" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              ) : (
                <svg className="w-5 h-5 text-red-500 mr-3" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              )}
              <span>{message.text}</span>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Informations générales */}
          <div className="bg-white rounded-lg border border-gray-200">
            <div className="p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Informations générales</h2>
              
              <div className="space-y-4">
                {/* Type d'événement */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Type d'événement *
                    </label>
                    <select
                      name="type"
                      value={formData.type}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="enterrement">Enterrement</option>
                      <option value="autre">Autre</option>
                    </select>
                  </div>

                  {formData.type === 'autre' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Nom du type *
                      </label>
                      <input
                        type="text"
                        name="nom_type_custom"
                        value={formData.nom_type_custom}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Ex: Mariage, Conférence..."
                      />
                    </div>
                  )}
                </div>

                {/* Bénéficiaire */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Bénéficiaire
                  </label>
                  <select
                    name="beneficiaire_id"
                    value={formData.beneficiaire_id}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Sélectionner un bénéficiaire</option>
                    {beneficiaires.map(benef => (
                      <option key={benef.id} value={benef.id}>
                        {benef.nom} {benef.prenom}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Pack d'enterrement */}
                {formData.type === 'enterrement' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Pack d'enterrement
                    </label>
                    <select
                      name="pack_enterrement_id"
                      value={formData.pack_enterrement_id}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">Sélectionner un pack (optionnel)</option>
                      {packsEnterrement.map(pack => (
                        <option key={pack.id} value={pack.id}>
                          {pack.nom}
                        </option>
                      ))}
                    </select>
                    {formData.pack_enterrement_id && (
                      <p className="mt-2 text-sm text-blue-600">
                        Les matériels du pack seront automatiquement inclus
                      </p>
                    )}
                  </div>
                )}

                {/* Transport */}
                <div className="flex items-center p-3 border border-gray-200 rounded-md">
                  <input
                    type="checkbox"
                    name="transport"
                    checked={formData.transport}
                    onChange={handleChange}
                    className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                  />
                  <label className="ml-3 text-sm font-medium text-gray-700">
                    Transport nécessaire
                  </label>
                </div>
              </div>
            </div>
          </div>

          {/* Section Matériels */}
          {(!formData.pack_enterrement_id || formData.type !== 'enterrement') && (
            <div className="bg-white rounded-lg border border-gray-200">
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold text-gray-900">Matériels</h2>
                  {formData.materiels.length > 0 && (
                    <span className="px-3 py-1 bg-blue-100 text-blue-800 text-sm font-medium rounded-full">
                      {formData.materiels.length} matériel(s)
                    </span>
                  )}
                </div>

                {/* Formulaire d'ajout */}
                <div className=" p-4 rounded-md mb-6">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Matériel
                      </label>
                      <select
                        name="materiel_id"
                        value={nouveauMateriel.materiel_id}
                        onChange={handleMaterielChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="">Sélectionner un matériel</option>
                        {materielsFiltres.map(materiel => (
                          <option key={materiel.id} value={materiel.id}>
                            {materiel.nom}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Quantité
                      </label>
                      <input
                        type="number"
                        name="quantite"
                        value={nouveauMateriel.quantite}
                        onChange={handleMaterielChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        min="1"
                      />
                    </div>
                    <div className="flex items-end">
                      <button
                        type="button"
                        onClick={ajouterMateriel}
                        disabled={!nouveauMateriel.materiel_id}
                        className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Ajouter
                      </button>
                    </div>
                  </div>
                </div>

                {/* Liste des matériels */}
                {formData.materiels.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                            Matériel
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                            Référence
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                            Quantité
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                            Stock
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {formData.materiels.map((materiel) => {
                          const materielOriginal = materielsDisponibles.find(m => m.id === materiel.id);
                          
                          return (
                            <tr key={materiel.id}>
                              <td className="px-4 py-3">
                                <div className="font-medium text-gray-900">{materiel.nom}</div>
                              </td>
                              <td className="px-4 py-3">
                                <span className="text-sm text-gray-600">{materiel.reference}</span>
                              </td>
                              <td className="px-4 py-3">
                                <div className="flex items-center space-x-2">
                                  <button
                                    type="button"
                                    onClick={() => modifierQuantiteMateriel(materiel.id, -1)}
                                    className="w-6 h-6 flex items-center justify-center border border-gray-300 rounded hover:"
                                  >
                                    -
                                  </button>
                                  <span className="w-8 text-center">{materiel.quantite}</span>
                                  <button
                                    type="button"
                                    onClick={() => modifierQuantiteMateriel(materiel.id, 1)}
                                    disabled={materiel.quantite >= (materielOriginal?.stock || 0)}
                                    className="w-6 h-6 flex items-center justify-center border border-gray-300 rounded hover: disabled:opacity-50"
                                  >
                                    +
                                  </button>
                                </div>
                              </td>
                              <td className="px-4 py-3">
                                <span className={`text-sm ${materiel.quantite <= (materielOriginal?.stock || 0) ? 'text-green-600' : 'text-red-600'}`}>
                                  {materielOriginal?.stock || 0}
                                </span>
                              </td>
                              <td className="px-4 py-3">
                                <button
                                  type="button"
                                  onClick={() => supprimerMateriel(materiel.id)}
                                  className="text-red-600 hover:text-red-900"
                                >
                                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                  </svg>
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <svg className="w-12 h-12 mx-auto text-gray-300 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                    </svg>
                    <p>Aucun matériel ajouté</p>
                    <p className="text-sm mt-1">Commencez par ajouter des matériels ci-dessus</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Boutons d'action */}
          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={() => navigate('/evenements')}
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center"
            >
              {loading ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Création...
                </>
              ) : (
                'Créer l\'événement'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreerEvenement;